import { useState, useEffect } from "react";
import axios from "axios";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from '../../../Axios'


export default function ManageHostel() {
  const [hostels, setHostels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [isEditing, setIsEditing] = useState(false);  // Manage edit mode
  const [showModal, setShowModal] = useState(false);

  // Fetch hostels from API
  useEffect(() => {
    const fetchHostels = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_BASE_URL}/api/v1/hostels/adminhostels`);
        setHostels(response.data.hostels); // Ensure the API returns a `hostels` field
        setLoading(false);
      } catch (err) {
        setError(err.message || "Failed to fetch hostels");
        setLoading(false);
      }
    };

    fetchHostels();
  }, []);

  const handleViewClick = (hostel) => {
    setSelectedHostel(hostel);
    setShowModal(true);
    setIsEditing(false);  // Disable edit mode for view
  };

  const handleEditClick = (hostel) => {
    setSelectedHostel({ ...hostel });
    setIsEditing(true);  // Enable edit mode
    setShowModal(true);
  };

  const handleInputChange = (field, value) => {
    setSelectedHostel((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleUpdateHostel = async () => {
    try {
      const response = await api.put(
        `/api/v1/hostels/adminhostels/${selectedHostel._id}`,
        selectedHostel
      );
      setHostels((prevHostels) =>
        prevHostels.map((hostel) =>
          hostel._id === selectedHostel._id ? { ...hostel, ...selectedHostel } : hostel
        )
      );
      setIsEditing(false);
      toast.success("Hostel updated successfully!");
    } catch (error) {
      console.error("Error updating hostel:", error);
      toast.error("Failed to update the hostel.");
    }
  };

  const handleDeleteClick = async (hostelId) => {
    if (!window.confirm("Are you sure you want to delete this hostel?")) return;

    try {
      await api.delete(`/api/v1/hostels/adminhostels/${hostelId}`);
      setHostels((prevHostels) => prevHostels.filter((hostel) => hostel._id !== hostelId));
      toast.success("Hostel deleted successfully!");
    } catch (error) {
      console.error("Error deleting hostel:", error);
      toast.error("Failed to delete the hostel.");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedHostel(null);
    setIsEditing(false);
  };

  if (loading) {
    return <div>Loading hostels...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-8">
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Banner */}
      <div className="bg-[#00BFA5] text-black text-center font-bold py-4 px-6 max-w-4xl mx-auto text-xl sm:text-2xl md:text-3xl rounded-lg shadow-lg">
        Hostel Directory - Manage Hostels
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white shadow-lg rounded-lg border-t-4 border-teal-500">
        <table className="min-w-full table-auto border-collapse relative">
          <thead>
            <tr className="bg-[#00BFA5] text-white">
              <th className="hidden sm:table-cell px-2 py-3 text-left text-xs sm:text-sm font-semibold">S No.</th>
              <th className="px-2 py-3 text-left font-semibold ">Hostel Name</th>
              <th className="hidden sm:table-cell px-2 py-3 text-left font-semibold ">Type</th>
              <th className="px-2 py-3 text-left font-semibold">Capacity</th>
              <th className="px-2 py-3 text-left font-semibold">Available Rooms</th>
              <th className="hidden sm:table-cell px-2 py-3 text-left font-semibold">Fees</th>
              <th className="px-2 py-3 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {hostels.map((hostel, index) => (
              <tr key={hostel._id} className="border-b hover:bg-teal-50 transition duration-200 ease-in-out">
                <td className="hidden sm:table-cell px-2 py-3 text-gray-700 font-medium">{index + 1}</td>
                <td className="px-2 py-3 text-gray-700 font-medium">{hostel.hostelName}</td>
                <td className="hidden sm:table-cell px-2 py-3 capitalize">{hostel.type}</td>
                <td className="px-2 py-3">{hostel.capacity}</td>
                <td className="px-2 py-3">{hostel.roomsAvailable}</td>
                <td className="hidden sm:table-cell px-2 py-3">₹{hostel.fees}</td>
                <td className="px-2 py-3 text-center space-x-3">
                  <button
                    onClick={() => handleViewClick(hostel)}
                    className="text-blue-500 hover:text-blue-700 transition duration-300"
                  >
                    <FaEye size={18} />
                  </button>
                  <button
                    onClick={() => handleEditClick(hostel)}
                    className="text-teal-500 hover:text-teal-700 transition duration-300"
                  >
                    <FaEdit size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(hostel._id)}
                    className="text-red-500 hover:text-red-700 transition duration-300"
                  >
                    <FaTrash size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal for Hostel Details */}
      {showModal && selectedHostel && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 p-6 relative">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={closeModal}>
              &times;
            </button>
            <h3 className="text-2xl font-semibold text-teal-600 mb-4">
              {isEditing ? "Edit Hostel" : selectedHostel.hostelName}
            </h3>
            <div className="space-y-2 text-gray-700">
              {isEditing ? (
                <>
                  <div>
                    <label>Hostel Name</label>
                    <input
                      type="text"
                      value={selectedHostel.hostelName}
                      onChange={(e) => handleInputChange("hostelName", e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label>Type</label>
                    <input
                      type="text"
                      value={selectedHostel.type}
                      onChange={(e) => handleInputChange("type", e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label>Capacity</label>
                    <input
                      type="number"
                      value={selectedHostel.capacity}
                      onChange={(e) => handleInputChange("capacity", e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label>Fees</label>
                    <input
                      type="number"
                      value={selectedHostel.fees}
                      onChange={(e) => handleInputChange("fees", e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <button
                    onClick={handleUpdateHostel}
                    className="mt-4 bg-teal-500 text-white p-2 rounded"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <strong>Type:</strong> {selectedHostel.type}
                  </div>
                  <div>
                    <strong>Capacity:</strong> {selectedHostel.capacity}
                  </div>
                  <div>
                    <strong>Fees:</strong> ₹{selectedHostel.fees}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
